/* global gsap, Physics2DPlugin */
/**
 * Stars Animation
 * Canvas-based particle system using GSAP
 * Based on: https://codepen.io/alexwiehl/pen/ZYbdxYw
 */
export default class StarsAnimation {
    constructor(canvasElement, panelElement, densityFactor = 1200) {
        // Density factor: container area / desired sprite count
        // Higher = fewer sprites. 1200 ≈ 1728 sprites on 1920x1080, 208 on 375x667
        this.densityFactor = densityFactor;
        // Accept elements directly (not IDs) for multiple instance support
        this.panel = panelElement;
        this.canvas = canvasElement;

        if (!this.panel || !this.canvas) {
            console.warn('Stars animation: panel or canvas element not provided');
            return;
        }

        // Get context with null check
        this.ctx = this.canvas.getContext('2d');

        if (!this.ctx) {
            console.warn('Stars animation: unable to get 2d context');
            return;
        }

        // Get dimensions with fallbacks and validation
        const computedStyle = window.getComputedStyle(this.panel);
        this.width = parseFloat(computedStyle.width) || this.panel.clientWidth || 0;
        this.height = parseFloat(computedStyle.height) || this.panel.clientHeight || 0;

        // Validate dimensions
        if (this.width === 0 || this.height === 0 || Number.isNaN(this.width) || Number.isNaN(this.height)) {
            console.warn('Stars animation: invalid panel dimensions', this.width, 'x', this.height);
            return;
        }

        // Default DPR to 1 instead of 0.5
        this.resolution = window.devicePixelRatio || 1;
        this.toRad = Math.PI / 180;
        // Check Physics2D plugin availability
        this.hasPhysics2D = false;
        // Spawner system for continuous generation
        this.maxSprites = this.calculateSpriteCount();
        this.activeSprites = []; // Currently visible sprites
        this.spritePool = []; // Reusable sprite objects
        this.spawnInterval = null;
        this.spawnRate = 100; // Spawn every 100ms (10 sprites/second)
        // Lifecycle state
        this.isInitialized = false;
        this.tickerFunction = null;
        // Texture cache for memory optimization
        this.textureCache = new Map();
        this.resizeCanvas();
        // Don't auto-init - controller will call init() when visible
    }

    calculateSpriteCount() {
        // Use actual container dimensions (not viewport)
        // Container may not fill entire viewport
        const containerArea = this.width * this.height;
        const spriteCount = Math.floor(containerArea / this.densityFactor);
        // Enforce reasonable bounds: minimum 100, maximum 2000
        return Math.max(100, Math.min(2000, spriteCount));
    }

    init() {
        // Guard against double-initialization
        if (this.isInitialized) {
            return;
        }
        // Check if GSAP is available
        if (typeof gsap === 'undefined') {
            console.warn('GSAP not loaded, stars animation disabled');
            return;
        }
        // Register Physics2DPlugin if available
        if (gsap.registerPlugin && window.Physics2DPlugin) {
            gsap.registerPlugin(Physics2DPlugin);
            this.hasPhysics2D = true;
        } else {
            console.warn('Stars animation: Physics2DPlugin not available, using fallback motion');
        }

        // Pre-allocate sprite pool (reusable objects)
        this.createSpritePool(this.maxSprites);
        // Start continuous spawner
        this.startSpawner();
        // Start render loop
        this.tickerFunction = () => this.render();
        gsap.ticker.add(this.tickerFunction);
        this.isInitialized = true;
    }

    /**
     * Pre-allocate sprite pool for object reuse
     */
    createSpritePool(count) {
        for (let i = 0; i < count; i += 1) {
            const widthSpr = this.randomInt(15, 35);
            const heightSpr = this.randomInt(15, 35);
            const texture = this.createShape(widthSpr, heightSpr, i);
            // Create sprite object without animation
            const sprite = {
                texture,
                width: (texture.naturalWidth || texture.width || 0) / this.resolution,
                height: (texture.naturalHeight || texture.height || 0) / this.resolution,
                alpha: 0,
                rotation: 0,
                scale: 1,
                originX: 0.5,
                originY: 0.5,
                x: 0,
                y: 0,
                animation: null,
            };
            this.spritePool.push(sprite);
        }
    }

    /**
     * Start continuous sprite spawner
     */
    startSpawner() {
        if (this.spawnInterval) {
            clearInterval(this.spawnInterval);
        }

        this.spawnInterval = setInterval(() => {
            // Only spawn if we have pool sprites and aren't at max active
            if (this.spritePool.length > 0 && this.activeSprites.length < this.maxSprites) {
                const sprite = this.spritePool.pop();
                this.activateSprite(sprite);
                this.activeSprites.push(sprite);
            }
        }, this.spawnRate);
    }

    /**
     * Activate a sprite from the pool with a new animation
     */
    activateSprite(sprite) {
        // Reset sprite to spawn point (intentionally mutating parameter)
        /* eslint-disable no-param-reassign */
        sprite.x = this.randomNr(0, this.width);
        sprite.y = this.randomNr(0, this.height);
        sprite.alpha = 0;
        sprite.rotation = this.randomNr(360);
        sprite.scale = this.randomNr(0.3, 2.5);
        // Animation parameters
        const duration = this.randomNr(3, 8);
        const fadeInDur = this.randomNr(0.5, 1.5);
        const fadeOutDur = this.randomNr(1, 2.5);
        const velocity = this.randomNr(4, 12);
        const angle = this.randomNr(0, 360);
        const endScale = this.randomNr(0.2, 0.8);
        const rotationEnd = 180 * this.randomNr(0, 2);
        // Create timeline that recycles sprite when complete
        sprite.animation = gsap.timeline({ onComplete: () => this.recycleSprite(sprite) });
        // Fade in (GSAP 3 syntax: duration inside vars object)
        sprite.animation.to(sprite, { duration: fadeInDur, alpha: 1, ease: 'power2.out' }, 0);

        // Motion with physics (if available)
        if (this.hasPhysics2D) {
            sprite.animation.to(
                sprite,
                {
                    duration, rotation: rotationEnd, scale: endScale, physics2D: { velocity, angle, gravity: 0.1 },
                },
                0,
            );
        } else {
            // Fallback without physics
            sprite.animation.to(sprite, { duration, rotation: rotationEnd, scale: endScale }, 0);
        }

        // Fade out - start near the end to give star time at full brightness
        const fadeOutStartTime = duration - fadeOutDur;
        sprite.animation.to(sprite, { duration: fadeOutDur, alpha: 0, ease: 'power2.in' }, fadeOutStartTime);
        /* eslint-enable no-param-reassign */
    }

    /**
     * Recycle sprite back to pool
     */
    recycleSprite(sprite) {
        // Remove from active list
        const index = this.activeSprites.indexOf(sprite);
        if (index > -1) {
            this.activeSprites.splice(index, 1);
        }
        // Kill timeline (intentionally mutating sprite.animation)
        if (sprite.animation) {
            sprite.animation.kill();
            sprite.animation = null; // eslint-disable-line no-param-reassign
        }

        // Return to pool for reuse
        this.spritePool.push(sprite);
    }

    createShape(widthSpr, heightSpr, i) {
        // Determine color variation based on index
        let colorVariant = 'default';
        if (i % 3 === 0) {
            colorVariant = 'cyan';
        } else if (i % 5 === 0) {
            colorVariant = 'purple';
        }

        // Create cache key
        const cacheKey = `${widthSpr}x${heightSpr}-${colorVariant}`;
        // Check cache first
        if (this.textureCache.has(cacheKey)) {
            return this.textureCache.get(cacheKey);
        }

        // Create new texture
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        // Guard against null context (hardened browsers)
        if (!context) {
            console.warn('Stars animation: unable to create offscreen 2d context');
            return canvas;
        }

        // Scale canvas for high-DPR displays
        canvas.width = widthSpr * this.resolution;
        canvas.height = heightSpr * this.resolution;
        // Scale context to match resolution (fixes retina display rendering)
        context.scale(this.resolution, this.resolution);
        // Now all coordinates are in logical pixels
        const radius = widthSpr / 2;
        const gradient = context.createRadialGradient(radius, radius, 0, radius, radius, radius);
        // Color variations
        if (colorVariant === 'cyan') {
            gradient.addColorStop(0, 'rgba(177,255,252,0.75)');
            gradient.addColorStop(0.15, 'rgba(177,255,252,0.1)');
        } else if (colorVariant === 'purple') {
            gradient.addColorStop(0, 'rgba(202,76,255,0.6)');
            gradient.addColorStop(0.1, 'rgba(202,76,255,0.1)');
        } else {
            gradient.addColorStop(0, 'rgba(102,219,214,0.6)');
            gradient.addColorStop(0.1, 'rgba(102,219,214,0.1)');
        }

        gradient.addColorStop(0.65, 'rgba(0,0,0,0)');
        context.fillStyle = gradient;
        context.fillRect(0, 0, widthSpr, heightSpr);
        // Store in cache
        this.textureCache.set(cacheKey, canvas);
        return canvas;
    }

    render() {
        this.ctx.clearRect(0, 0, this.width, this.height);

        // Only render active sprites (not the entire pool)
        for (let i = 0; i < this.activeSprites.length; i += 1) {
            const sprite = this.activeSprites[i];
            // Skip if no alpha
            if (sprite.alpha) {
                this.ctx.save();
                const offsetX = sprite.originX * sprite.width;
                const offsetY = sprite.originY * sprite.height;
                this.ctx.translate(sprite.x + offsetX, sprite.y + offsetY);
                this.ctx.rotate(sprite.rotation * this.toRad);
                this.ctx.scale(sprite.scale, sprite.scale);
                this.ctx.globalAlpha = sprite.alpha;
                this.ctx.drawImage(sprite.texture, -offsetX, -offsetY);
                this.ctx.restore();
            }
        }
    }

    resizeCanvas() {
        // CRITICAL: Setting canvas.width/height resets the context transform to identity
        // So we don't need setTransform() here - the transform is already reset
        this.canvas.width = this.width * this.resolution;
        this.canvas.height = this.height * this.resolution;
        this.canvas.style.width = `${this.width}px`;
        this.canvas.style.height = `${this.height}px`;
        // Scale context for retina displays (happens AFTER canvas.width reset the transform)
        this.ctx.scale(this.resolution, this.resolution);
    }

    // eslint-disable-next-line class-methods-use-this
    randomNr(min, max) {
        let minVal = min;
        let maxVal = max;
        if (maxVal === undefined) {
            maxVal = minVal;
            minVal = 0;
        }
        if (minVal > maxVal) {
            const tmp = minVal;
            minVal = maxVal;
            maxVal = tmp;
        }
        return minVal + (maxVal - minVal) * Math.random();
    }

    // eslint-disable-next-line class-methods-use-this
    randomInt(min, max) {
        let minVal = min;
        let maxVal = max;
        if (maxVal === undefined) {
            maxVal = minVal;
            minVal = 0;
        }
        if (minVal > maxVal) {
            const tmp = minVal;
            minVal = maxVal;
            maxVal = tmp;
        }
        return Math.floor(minVal + (maxVal - minVal + 1) * Math.random());
    }

    /**
     * Handle resize events - updates canvas dimensions and context scaling
     * CRITICAL: Must reset transform before resizing to avoid cumulative scaling
     */
    handleResize() {
        if (!this.panel || !this.canvas || !this.ctx) return;

        // Get new dimensions
        const computedStyle = window.getComputedStyle(this.panel);
        this.width = parseFloat(computedStyle.width) || this.panel.clientWidth || 0;
        this.height = parseFloat(computedStyle.height) || this.panel.clientHeight || 0;
        // Check if DPR changed (moving to different monitor or zoom)
        const newResolution = window.devicePixelRatio || 1;
        const dprChanged = newResolution !== this.resolution;
        if (dprChanged) {
            this.resolution = newResolution;
            // Clear texture cache - textures are at wrong resolution
            this.textureCache.clear();
            // Note: Textures will be regenerated on-demand as sprites spawn
        }
        // Validate
        if (this.width === 0 || this.height === 0 || Number.isNaN(this.width) || Number.isNaN(this.height)) {
            return;
        }

        // Reset context transform BEFORE scaling (fixes cumulative scale bug)
        // ctx.scale() is cumulative - must reset to identity matrix first
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        // Resize canvas with new dimensions
        this.resizeCanvas();
    }

    /**
     * Destroy the animation and free resources
     */
    destroy() {
        // Stop spawner
        if (this.spawnInterval) {
            clearInterval(this.spawnInterval);
            this.spawnInterval = null;
        }
        // Stop rendering
        if (this.tickerFunction && typeof gsap !== 'undefined') {
            gsap.ticker.remove(this.tickerFunction);
        }
        // Kill all active sprite timelines
        if (this.activeSprites && this.activeSprites.length > 0) {
            this.activeSprites.forEach((sprite) => {
                if (sprite.animation) {
                    sprite.animation.kill();
                    sprite.animation = null; // eslint-disable-line no-param-reassign
                }
            });
        }

        // Clear arrays
        this.activeSprites = [];
        this.spritePool = [];

        // Clear canvas
        if (this.ctx && this.canvas) {
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }
        // Clear texture cache
        if (this.textureCache) {
            this.textureCache.clear();
        }

        // Reset state
        this.isInitialized = false;
        this.tickerFunction = null;
    }
}
